  import React, { useState, useRef, useEffect, useCallback } from "react";
  import { NavLink } from "react-router-dom";
  import { getAnchoredMenuStyle, useCloseOnScroll } from "../Utils/anchoredMenu";
  import {
    getAllUserBooking,
    uploadTransitSingleFile,
  } from "../Services/userService";
  import { RoutesPaths, logisticTypes, STATUS_OPTIONS } from "../Router/constant";
  import Skeleton from "../Components/SkeletonLoader";
  import ConfirmationModal from "../Components/ConfirmationModal";
  import { updateBookingStatus } from "../Services/bookingService";
  import { toast } from "react-toastify";
  import {
    FiBox,
    FiChevronDown,
    FiChevronLeft,
    FiChevronRight,
    FiChevronUp,
    FiEdit2,
    FiEye,
    FiHome,
    FiPlus,
    FiX,
  } from "react-icons/fi";
  import { MdDirectionsBoat, MdEngineering, MdEventNote, MdTaskAlt } from "react-icons/md";
  import {
    generateDeliveryOrderPdf,
    generateWorkOrderPdf,
  } from "../Utils/pdfGenerator";

  export default function Bookingmanagement() {
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [sortOrder, setSortOrder] = useState("desc");

    const fileInputRef = useRef(null);
    const dropdownRef = useRef(null);

    const [bookingUserData, setBookingUserData] = useState(null);

    const [limit] = useState(10);
    const [offset, setOffset] = useState(1);

    const [activeProgress, setActiveProgress] = useState(null);

    const [confirmation, setConfirmation] = useState({
      show: false,
      orderId: null,
      statusValue: "",
    });

    const [activeDropdown, setActiveDropdown] = useState(null);
    const [loading, setLoading] = useState(true);

    /*
    * FILTER STATE
    *
    * As soon as any value changes,
    * useEffect will call API.
    */
    const [filters, setFilters] = useState({
      logisticsType: "",
      status: "",
      fromDate: "",
      toDate: "",
    });

    const userRole = localStorage.getItem("userRole");

    /*
    * ==========================================
    * STATUS DROPDOWN
    * ==========================================
    */

    const [dropdownStyle, setDropdownStyle] = useState(null);
    const toggleDropdown = (id, event) => {
      const opening = activeDropdown !== id;
      setDropdownStyle(opening ? getAnchoredMenuStyle(event?.currentTarget) : null);
      setActiveDropdown(opening ? id : null);
    };
    const closeDropdown = useCallback(() => setActiveDropdown(null), []);
    useCloseOnScroll(Boolean(activeDropdown), closeDropdown);

    /*
    * ==========================================
    * FILE UPLOAD
    * ==========================================
    */

    const handleFileChange = async (event) => {
      const file = event.target.files[0];

      if (!file) return;

      setConfirmation((prev) => ({
        ...prev,
        attachment: file,
      }));

      try {
        const formData = new FormData();

        formData.append("file", file);

        const response = await uploadTransitSingleFile(formData);

        console.log(response, "response");

        if (response?.data?.status === true) {
          const fileName = response.data.data.fileName;

          setConfirmation((prev) => ({
            ...prev,
            uploadedFileName: fileName,
          }));
        } else {
          toast.error("File upload failed");
        }
      } catch (error) {
        console.error("File upload error:", error);

        toast.error("File upload error");
      }
    };

    /*
    * ==========================================
    * FETCH BOOKING
    * ==========================================
    */

    const fetchBookingData = async () => {
      setLoading(true);

      const params = {
        offset,
        limit,
        sort: sortOrder,

        status: filters.status,
        logisticsType: filters.logisticsType,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      };

      try {
        /*
        * Existing partner condition
        */
        if (userRole === "partner") {
          params.isPartner = true;
          params.partnerId = localStorage.getItem("partnerId");
        }

        const bookingResp = await getAllUserBooking(params);

        setBookingUserData(bookingResp?.data);
      } catch (error) {
        console.error("Error fetching booking:", error);
      } finally {
        setLoading(false);
      }
    };

    /*
    * API CALL:
    *
    * - pagination change
    * - sort change
    * - logistics filter change
    * - status change
    * - from date change
    * - to date change
    */
    useEffect(() => {
      fetchBookingData();
    }, [
      offset,
      sortOrder,
      filters.status,
      filters.logisticsType,
      filters.fromDate,
      filters.toDate,
    ]);

    /*
    * ==========================================
    * FILTER CHANGE
    * ==========================================
    */

    const handleFilterChange = (event) => {
      const { name, value } = event.target;

      /*
      * Date validation
      */
      if (
        name === "fromDate" &&
        filters.toDate &&
        value &&
        value > filters.toDate
      ) {
        toast.error("From date cannot be greater than To date");

        return;
      }

      if (
        name === "toDate" &&
        filters.fromDate &&
        value &&
        value < filters.fromDate
      ) {
        toast.error("To date cannot be less than From date");

        return;
      }

      /*
      * Reset pagination when filter changes
      */
      setOffset(1);

      /*
      * Updating this state automatically
      * triggers useEffect API call.
      */
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    /*
    * ==========================================
    * CLEAR FILTER
    * ==========================================
    */

    const handleClearFilters = () => {
      setOffset(1);

      setFilters({
        logisticsType: "",
        status: "",
        fromDate: "",
        toDate: "",
      });
    };

    /*
    * ==========================================
    * CLICK OUTSIDE
    * ==========================================
    */

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setActiveDropdown(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    /*
    * ==========================================
    * BOOKING DATA
    * ==========================================
    */

    const orders = bookingUserData?.data?.orders || [];

    const count = bookingUserData?.data?.count || 0;

    const totalPages = Math.ceil(count / limit);

    const currentPage = offset;

    /*
    * ==========================================
    * PAGINATION
    * ==========================================
    */

    const generatePagination = (currentPage, totalPages) => {
      const pages = [];

      if (totalPages <= 4) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 2) {
          pages.push(1, 2, "...", totalPages - 1, totalPages);
        } else if (currentPage <= 4) {
          pages.push(3, 4, "...", totalPages - 1, totalPages);
        } else if (currentPage >= totalPages - 2) {
          for (let i = totalPages - 2; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(
            currentPage,
            currentPage + 1,
            "...",
            totalPages - 1,
            totalPages,
          );
        }
      }

      return pages;
    };

    /*
    * ==========================================
    * HANDLE STATUS
    * ==========================================
    */

    const handleStatusClick = (orderId, statusValue) => {
      setConfirmation({
        show: true,
        orderId,
        statusValue,

        customStatus:
          statusValue.toLowerCase() === "in transit" ? "" : null,

        messageText:
          statusValue.toLowerCase() === "in transit" ? "" : null,
      });

      setActiveDropdown(null);
    };

    /*
    * ==========================================
    * CONFIRM STATUS
    * ==========================================
    */

    const handleConfirm = async () => {
      const { orderId, statusValue, customStatus, messageText } = confirmation;

      let payload;

      if (statusValue.toLowerCase() === "in transit") {
        payload = {
          status: "in transit",
          customStatus: customStatus || "",
          inTransitMessage: messageText || "",
          attachment: confirmation.uploadedFileName || "",
        };
      } else {
        payload = {
          status: statusValue,
        };
      }

      try {
        setConfirmLoading(true);

        const response = await updateBookingStatus(orderId, payload);

        if (response?.data?.status) {
          toast.success(response?.data?.data?.message);

          setActiveDropdown(null);

          setConfirmation({
            show: false,
            orderId: null,
            statusValue: "",
            customStatus: "",
            messageText: "",
            attachment: "",
            uploadedFileName: "",
          });

          /*
          * Current filters remain applied
          */
          fetchBookingData();
        } else {
          toast.error(
            response?.data?.data?.message || "Failed to update order status.",
          );
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.data?.message ||
            "An unexpected error occurred.",
        );
      } finally {
        setConfirmLoading(false);
      }
    };

    /*
    * ==========================================
    * FORMAT LOGISTICS
    * ==========================================
    */

    const formatLogisticsType = (type = "") =>
      String(type)
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/^./, (char) => char.toUpperCase());

    /*
    * ==========================================
    * STATUS BADGE
    * ==========================================
    */

    const formatStatusBadge = (status) => {
      const map = {
        pending: "Pending",
        completed: "Completed",
        inprogress: "In-progress",
        "in progress": "In-progress",

        "order placed": "Order Placed",
        "submitted for quote": "Submitted for Quote",

        "quote in place and sent to client for approval":
          "Quote Sent to Client",

        "quote approved by client": "Quote Approved",

        "quote rejected by client": "Quote Rejected",

        "order accepted": "Order Accepted",

        "product shipped": "Product Shipped",

        "in-transit": "In-Transit",

        "in transit": "In-Transit",

        delivered: "Delivered",
      };

      return map[String(status).toLowerCase()] || status;
    };

    /*
    * ==========================================
    * FORMAT PROGRESS
    * ==========================================
    */

    const formatStatus = (status = "") => {
      const map = {
        "order placed": "Order placed",

        "submitted for quote": "Submitted for Quote",

        "quote in place and sent to client for approval":
          "Quote Sent to Client for Approval",

        "quote approved by client":
          "Quote Approved by Client",

        "quote rejected by client":
          "Quote Rejected by Client",

        "order accepted": "Order Accepted",

        "product shipped": "Product Shipped",

        "in transit": "In-transit",

        "in-transit": "In-transit",

        delivered: "Delivered",
      };

      return map[status.toLowerCase()] || status;
    };

    /*
    * ==========================================
    * DOWNLOAD WORK ORDER
    * ==========================================
    */

    const handleDownloadPdf = async (order) => {
      try {
        await generateWorkOrderPdf(order);
      } catch (err) {
        toast.error(err.message || "Something went wrong");
      }
    };

    /*
    * ==========================================
    * DOWNLOAD DELIVERY ORDER
    * ==========================================
    */

    const handleDownloadDeliveryOrder = async (order) => {
      try {
        await generateDeliveryOrderPdf(order);
      } catch (err) {
        toast.error(err.message || "Something went wrong");
      }
    };

    /*
    * Check whether any filter is active
    */
    const isFilterActive =
      filters.logisticsType ||
      filters.status ||
      filters.fromDate ||
      filters.toDate;

    const recordCount = (status) =>
      bookingUserData?.data?.records?.find((r) => r.status === status)?.count || 0;

    const summaryCards = [
      { label: "Total booking", value: count, tone: "orange", Art: MdDirectionsBoat },
      { label: "Pending booking", value: recordCount("pending"), tone: "amber", Art: MdEventNote },
      { label: "In-progress booking", value: recordCount("inprogress"), tone: "blue", Art: MdEngineering },
      { label: "Completed booking", value: recordCount("completed"), tone: "green", Art: MdTaskAlt },
    ];

    const resetConfirmation = () =>
      setConfirmation({
        show: false,
        orderId: null,
        statusValue: "",
        customStatus: "",
        messageText: "",
        attachment: "",
        uploadedFileName: "",
      });

    const isTransitStatus = confirmation.statusValue?.toLowerCase() === "in transit";

    const statusLabel = (confirmation.statusValue || "").toLowerCase().replace(/_/g, " ");

    return (
      <div className="container-fluid qd-page">
        {/* Breadcrumb */}
        <nav className="qd-breadcrumb">
          <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
            <FiHome />
          </NavLink>
          <FiChevronRight />
          <span className="qd-breadcrumb-current">Booking management</span>
        </nav>

        {/* Summary cards */}
        <div className="qd-summary-grid qb-summary-grid">
          {summaryCards.map(({ label, value, tone, Art }) => (
            <div className={`qd-card qd-summary qd-summary-${tone} qb-summary`} key={label}>
              <div className="qd-stat-body">
                {loading ? (
                  <>
                    <div className="qd-skel" style={{ width: 70, height: 36, marginBottom: 8 }} />
                    <div className="qd-skel" style={{ width: 120, height: 16 }} />
                  </>
                ) : (
                  <>
                    <h3 className="qb-summary-value">{value}</h3>
                    <p className="qb-summary-label">{label}</p>
                  </>
                )}
              </div>
              <span className="qb-summary-art" aria-hidden="true">
                <Art />
              </span>
            </div>
          ))}
        </div>

        {/* Booking table */}
        <div className="qd-card qd-panel">
          <div className="qd-panel-header">
            <div className="qd-panel-title">
              <span className="qd-panel-icon">
                <FiBox />
              </span>
              <div>
                <h6>Booking details</h6>
                <p className="qd-panel-sub">Manage and track all logistics bookings</p>
              </div>
            </div>
            {userRole === "admin" && (
              <NavLink to={RoutesPaths.ADDPUBLICORDER} className="qd-btn qd-btn-primary">
                <FiPlus /> Create order for new user
              </NavLink>
            )}
          </div>

          {/* Filters */}
          <div className="qb-filters">
            <label className="qb-filter">
              <span>Logistics type</span>
              <select
                className="qd-select"
                name="logisticsType"
                value={filters.logisticsType}
                onChange={handleFilterChange}
              >
                <option value="">All logistics</option>
                {Object.entries(logisticTypes || {}).map(([value, label]) => (
                  <option key={value} value={value}>
                    {formatLogisticsType(label)}
                  </option>
                ))}
              </select>
            </label>

            <label className="qb-filter">
              <span>Status</span>
              <select
                className="qd-select"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All status</option>
                <option value="pending">Pending</option>
                <option value="inprogress">In-progress</option>
                <option value="completed">Completed</option>
              </select>
            </label>

            <label className="qb-filter">
              <span>From date</span>
              <input
                type="date"
                className="qb-date"
                name="fromDate"
                value={filters.fromDate}
                max={filters.toDate || undefined}
                onChange={handleFilterChange}
              />
            </label>

            <label className="qb-filter">
              <span>To date</span>
              <input
                type="date"
                className="qb-date"
                name="toDate"
                value={filters.toDate}
                min={filters.fromDate || undefined}
                onChange={handleFilterChange}
              />
            </label>

            {isFilterActive && (
              <button type="button" className="qd-btn qd-btn-outline qb-clear" onClick={handleClearFilters}>
                <FiX /> Clear
              </button>
            )}
          </div>

          <div className="qd-table-wrap">
            <table className="qd-table qb-table">
              <thead>
                <tr>
                  <th>Order Id</th>
                  <th>Logistics type</th>
                  <th>
                    <button
                      type="button"
                      className="qb-sort"
                      onClick={() => {
                        // Keep filters, just change sorting.
                        setOffset(1);
                        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      }}
                      aria-label={`Sort by date ${sortOrder === "asc" ? "descending" : "ascending"}`}
                    >
                      Date
                      <span className="qb-sort-arrows">
                        <FiChevronUp className={sortOrder === "asc" ? "on" : ""} />
                        <FiChevronDown className={sortOrder === "desc" ? "on" : ""} />
                      </span>
                    </button>
                  </th>
                  <th>Order progress</th>
                  <th>Status</th>
                  <th className="qb-th-actions">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td><Skeleton width={150} /></td>
                      <td><Skeleton width={90} /></td>
                      <td><Skeleton width={80} /></td>
                      <td><Skeleton width={110} /></td>
                      <td><Skeleton width={70} height={24} /></td>
                      <td><Skeleton width={200} height={24} /></td>
                    </tr>
                  ))
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="qd-empty">No order found</td>
                  </tr>
                ) : (
                  orders.map((order, index) => {
                    const isDelivered = order?.orderHistory?.some(
                      (item) => item.status === "delivered" && item.completed,
                    );

                    const progressList = [
                      ...new Set(
                        (order.orderHistory || [])
                          .filter((h) => h.completed)
                          .map((h) => (h.status === "product shipped" ? "in transit" : h.status)),
                      ),
                    ].map((status) => formatStatus(status));

                    const editDisabled = userRole === "partner" && !order?.isAccepted;
                    const statusKey = String(order.status || "").toLowerCase().replace(/\s+/g, "");

                    return (
                      <tr key={`${order._id}-${index}`}>
                        <td>
                          <button
                            type="button"
                            className="qb-order-link"
                            onClick={() => handleDownloadPdf(order)}
                            title="Download work order"
                          >
                            #{order.orderId}
                          </button>
                        </td>

                        <td>{formatLogisticsType(logisticTypes[order.logisticsType]) || "N/A"}</td>

                        <td>{new Date(order.createdAt).toLocaleDateString("en-GB")}</td>

                        <td>
                          {progressList.length === 0 ? (
                            "-"
                          ) : (
                            <>
                              {progressList[progressList.length - 1]}
                              {progressList.length > 1 && (
                                <span
                                  className="qd-more"
                                  onClick={() => setActiveProgress(progressList)}
                                >
                                  +{progressList.length - 1} more
                                </span>
                              )}
                            </>
                          )}
                        </td>

                        <td>
                          <span className={`qd-status qd-status-${statusKey}`}>
                            {formatStatusBadge(order.status) || "N/A"}
                          </span>
                        </td>

                        <td>
                          <div className="qd-actions qb-actions position-relative">
                            <NavLink
                              to={`${RoutesPaths.BOOKINGMANAGEMENTORDERHISTORY}/${order?._id}`}
                              className="qb-icon-action"
                              title="View"
                              aria-label="View order"
                            >
                              <FiEye />
                            </NavLink>

                            <button
                              type="button"
                              className={`qb-icon-action ${editDisabled ? "disbaledEditBooking" : ""}`}
                              title="Update status"
                              aria-label="Update status"
                              onClick={(event) => {
                                if (editDisabled) return;
                                toggleDropdown(order._id, event);
                              }}
                            >
                              <FiEdit2 />
                            </button>

                            {isDelivered ? (
                              <button
                                type="button"
                                className="qd-btn qd-btn-primary qd-btn-sm qb-doc-btn"
                                onClick={() => handleDownloadDeliveryOrder(order)}
                              >
                                Delivery order
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="qd-btn qd-btn-primary qd-btn-sm qb-doc-btn"
                                onClick={() => handleDownloadPdf(order)}
                              >
                                Work order
                              </button>
                            )}

                            {activeDropdown === order._id && (
                              <div
                                className="actionDropdownOuter qd-anchored-menu"
                                ref={dropdownRef}
                                style={dropdownStyle || undefined}
                              >
                                <ul className="actionDropdownList">
                                  {STATUS_OPTIONS.map((status) => {
                                    const completedHistory =
                                      order.orderHistory?.filter((h) => h.completed) || [];

                                    const latestCompleted =
                                      completedHistory[completedHistory.length - 1]?.status.toLowerCase() || "";

                                    return (
                                      <li
                                        key={status}
                                        className={`actionDropdownItem ${
                                          latestCompleted === status.toLowerCase() ? "selected" : ""
                                        }`}
                                        onClick={() => handleStatusClick(order._id, status)}
                                      >
                                        {status.toLowerCase() === "in transit"
                                          ? "Other"
                                          : status.charAt(0).toUpperCase() + status.slice(1)}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {count > 0 && totalPages > 1 && (
            <div className="qd-pagination">
              <span className="qd-muted">
                Showing {(currentPage - 1) * limit + 1}-{Math.min(currentPage * limit, count)} of {count}
              </span>
              <div className="qd-pages">
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setOffset(currentPage - 1)}
                  aria-label="Previous"
                >
                  <FiChevronLeft />
                </button>
                {generatePagination(currentPage, totalPages).map((page, idx) =>
                  page === "..." ? (
                    <span key={idx} className="qd-ellipsis">...</span>
                  ) : (
                    <button
                      key={idx}
                      className={currentPage === page ? "active" : ""}
                      onClick={() => setOffset(page)}
                    >
                      {page}
                    </button>
                  ),
                )}
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setOffset(currentPage + 1)}
                  aria-label="Next"
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Progress modal */}
        {activeProgress && (
          <div className="progressModalOverlay" onClick={() => setActiveProgress(null)}>
            <div className="progressModal" onClick={(e) => e.stopPropagation()}>
              <h4>Order Progress</h4>
              <ul>
                {activeProgress.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ul>
              <div className="modalFooter">
                <button className="btn btn-cancel" onClick={() => setActiveProgress(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation modal */}
        <ConfirmationModal
          show={confirmation.show}
          title={isTransitStatus ? "Add custom tracking status" : "Update status"}
          message={
            isTransitStatus ? (
              <div className="qb-transit-form">
                <p>Please enter a custom tracking status for the user to see:</p>

                <input
                  type="text"
                  className="qb-modal-input"
                  placeholder="Custom tracking status"
                  value={confirmation.customStatus || ""}
                  onChange={(e) =>
                    setConfirmation({ ...confirmation, customStatus: e.target.value })
                  }
                />

                <textarea
                  className="qb-modal-input"
                  placeholder="Enter here message"
                  value={confirmation.messageText || ""}
                  onChange={(e) =>
                    setConfirmation({ ...confirmation, messageText: e.target.value })
                  }
                />

                <div className="qb-upload">
                  <input
                    type="file"
                    id="attachmentUpload"
                    name="file"
                    ref={fileInputRef}
                    accept="image/*,application/pdf"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />

                  <label htmlFor="attachmentUpload">Click to upload file</label>
                  <p>PNG, JPG, PDF, DOC (Max 5MB)</p>

                  {confirmation?.attachment && (
                    <div className="qb-upload-file">
                      <span>
                        📎{" "}
                        {typeof confirmation.attachment === "string"
                          ? confirmation.attachment
                          : confirmation.attachment?.name}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setConfirmation((prev) => ({
                            ...prev,
                            attachment: null,
                            uploadedFileName: "",
                          }))
                        }
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              `Are you sure you want to update the status to "${
                statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)
              }"?`
            )
          }
          onConfirm={handleConfirm}
          onCancel={resetConfirmation}
          confirmText={
            confirmLoading ? "Confirming..." : isTransitStatus ? "Save status" : "Confirm"
          }
          confirmDisabled={confirmLoading}
          cancelText="Cancel"
        />
      </div>
    );
  }